import {FormControl, NumberInput} from 'platform/components';

import {FieldPathByValue, FieldValues, useController} from 'react-hook-form';

import {isNumber} from 'ramda-adjunct';

import {Nullish, RequiredTestIdProps} from 'shared/web';

import {fromMinorUnits} from '../../../utils/fromMinorUnits';
import {getCurrencyFractionDigits} from '../../../utils/getCurrencyFractionDigits';
import {toMinorUnits} from '../../../utils/toMinorUnits';

interface VehicleSelectionPriceFieldProps<TFieldValues extends FieldValues>
  extends RequiredTestIdProps {
  control: FormControl<TFieldValues>;
  name: FieldPathByValue<TFieldValues, number | Nullish>;
  label: string;
  currency: string;
  isDisabled?: boolean;
}

// A price bound is stored and sent in minor units, which is what the BE compares, and typed in the
// currency's major units. Converting at the control keeps the form state in the API shape, so the
// campaign and the promotional photo mappers stay untouched.
export function VehicleSelectionPriceField<TFieldValues extends FieldValues>(
  props: VehicleSelectionPriceFieldProps<TFieldValues>
) {
  const {field, fieldState, formState} = useController({name: props.name, control: props.control});

  return (
    <NumberInput
      name={field.name}
      value={isNumber(field.value) ? fromMinorUnits(field.value, props.currency) : null}
      onChange={(amount) =>
        field.onChange(isNumber(amount) ? toMinorUnits(amount, props.currency) : null)
      }
      onBlur={field.onBlur}
      label={props.label}
      suffix={props.currency}
      decimalPlaces={getCurrencyFractionDigits(props.currency)}
      // The same lock as the platform FormField: a pending save already carries the old bound.
      isDisabled={field.disabled || props.isDisabled || formState.isSubmitting}
      errorMessage={fieldState.error?.message}
      data-testid={props['data-testid']}
    />
  );
}
